/**
 * The phone's Data tab: each view a card, swiped sideways, with the current
 * view's name in the middle of a strip that slides with the finger (owner,
 * 2026-10-14; `docs/features/mobile.md`, "On a phone the views are cards you
 * swipe"). The arithmetic is `lib/data-cards.ts`, tested there.
 *
 * - **Sideways is ours, up and down is the browser's.** `touch-action: pan-y`
 *   leaves vertical scrolling to the card, and the first few pixels decide
 *   which gesture this is (`axisLock`); a scroll then never moves a card.
 * - **Stops at the ends** with a little give — never wraps.
 * - **Only the card on screen and its neighbours are mounted**, so opening the
 *   tab does not fetch all eight views at once. A neighbour is mounted because
 *   its edge peeks in, and a blank edge reads as broken.
 */

import { useEffect, useRef, useState, type ReactNode } from "react";

import { axisLock, cardLeft, cardWidth, isMounted, labelOffset, resist, settle } from "../lib/data-cards";

export interface DataCardsProps {
  /** The views, in order: what each card is called, and its chip when two letters will not do. */
  labels: readonly { id: string; label: string; chip?: string }[];
  /** Which card is on screen, by id. */
  active: string;
  onActive: (id: string) => void;
  /** One card's contents. Called only for mounted cards. */
  render: (id: string) => ReactNode;
  /** Above the cards, under the strip — the peer trail while wandering. */
  header?: ReactNode;
  /**
   * The symbol the cards describe, named before the current view in the strip
   * (`MU · Fin St`) — no card but Profile says which company it is about
   * (owner, 2026-10-15).
   */
  symbol?: string;
  /** The title strip; off where each card names itself (the briefs). */
  strip?: boolean;
  /** The strip's name for the deck. */
  ariaLabel?: string;
}

const SLIDE = "transform 240ms cubic-bezier(0.22, 0.61, 0.36, 1)";

export function DataCards({
  labels, active, onActive, render, header, symbol, strip = true, ariaLabel = "Symbol data",
}: DataCardsProps) {
  const host = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const el = host.current;
    if (!el) return;
    setWidth(el.clientWidth);
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver((entries) => setWidth(entries[0]?.contentRect.width ?? 0));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const count = labels.length;
  const found = labels.findIndex((l) => l.id === active);
  const index = found < 0 ? 0 : found;

  const [drag, setDrag] = useState(0);
  const gesture = useRef<{ id: number; x: number; y: number; t: number; axis: "x" | "y" | null; lastX: number; lastT: number } | null>(null);

  const go = (next: number) => {
    const target = labels[Math.min(Math.max(next, 0), count - 1)];
    if (target && target.id !== active) onActive(target.id);
  };

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (gesture.current) return;
    gesture.current = { id: e.pointerId, x: e.clientX, y: e.clientY, t: e.timeStamp, axis: null, lastX: e.clientX, lastT: e.timeStamp };
  };
  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const g = gesture.current;
    if (!g || g.id !== e.pointerId) return;
    const dx = e.clientX - g.x;
    if (g.axis === null) {
      g.axis = axisLock(dx, e.clientY - g.y);
      if (g.axis === "x") e.currentTarget.setPointerCapture(e.pointerId);
    }
    if (g.axis !== "x") return;
    g.lastX = e.clientX;
    g.lastT = e.timeStamp;
    setDrag(resist(dx, index, count));
  };
  const end = (e: React.PointerEvent<HTMLDivElement>, cancelled: boolean) => {
    const g = gesture.current;
    if (!g || g.id !== e.pointerId) return;
    gesture.current = null;
    if (g.axis === "x" && !cancelled) {
      const dx = e.clientX - g.x;
      const dt = Math.max(1, e.timeStamp - g.lastT);
      const velocity = (e.clientX - g.lastX) / dt;
      go(settle(index, dx, velocity, count, width));
    }
    setDrag(0);
  };

  const dragging = drag !== 0;
  const w = cardWidth(width);

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column", minHeight: 0 }}>
      {/* The title strip (owner, 2026-10-15): symbol and current view in the
          middle at their own width; the next view faded at the right edge, the
          previous at the left, sharing what is left and clipped alike when it
          runs out. The row follows the finger; the names change on release. */}
      {strip && (
      <div
        role="tablist"
        aria-label={ariaLabel}
        style={{ height: 30, overflow: "hidden", flex: "0 0 auto" }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            height: "100%",
            padding: "0 10px",
            transform: `translateX(${labelOffset(0, 0, drag, width) * (width / 3)}px)`,
            transition: dragging ? "none" : SLIDE,
          }}
        >
          {[index - 1, index, index + 1].map((i) => {
            const l = labels[i];
            const on = i === index;
            const side: React.CSSProperties = { flex: "1 1 0", textAlign: i < index ? "left" : "right" };
            if (!l) return <span key={`gap${i}`} style={side} />;
            return (
              <button
                key={l.id}
                type="button"
                role="tab"
                aria-selected={on}
                onClick={() => go(i)}
                style={{
                  ...(on ? { flex: "0 1 auto", maxWidth: "70%" } : side),
                  minWidth: 0,
                  height: 26,
                  padding: 0,
                  border: 0,
                  background: "none",
                  font: "inherit",
                  fontSize: on ? 15 : 12,
                  fontWeight: on ? 700 : 400,
                  color: on ? "var(--qf-text)" : "var(--qf-text-muted)",
                  opacity: on ? 1 : 0.6,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  cursor: "pointer",
                }}
              >
                {on && symbol ? `${symbol} · ` : null}
                {l.label}
              </button>
            );
          })}
        </div>
      </div>
      )}

      {/* Every card by two letters: where you are, and one tap to anywhere —
          Fin St to Holdings without swiping through four cards (owner,
          2026-10-14). Replaced the dots, which said where but went nowhere. */}
      <div
        aria-label="Go to"
        style={{
          display: "flex", justifyContent: "center", gap: 4, padding: strip ? "0 8px 6px" : "6px 8px",
          flex: "0 0 auto", borderBottom: "1px solid var(--qf-border)",
        }}
      >
        {labels.map((l, i) => (
          <button
            key={l.id}
            type="button"
            aria-label={l.label}
            aria-current={i === index}
            onClick={() => go(i)}
            style={{
              minWidth: 34,
              height: 28,
              padding: "0 6px",
              borderRadius: 999,
              border: `1px solid ${i === index ? "var(--qf-accent)" : "var(--qf-border)"}`,
              background: i === index ? "var(--qf-accent)" : "transparent",
              color: i === index ? "var(--qf-background)" : "var(--qf-text-secondary)",
              font: "inherit",
              fontSize: 12,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            {l.chip ?? shortLabel(l.label)}
          </button>
        ))}
      </div>

      {header}
      {/* **No card may take the sideways swipe.** Each card scrolls up and down,
          so the browser judges a touch against the card, not this track — and
          any content wider than the card (a table in its own scroller) would
          pan instead of the cards moving. Cards are built to fit; this makes
          the swipe work even where one does not. */}
      <style>{"[data-qf-card], [data-qf-card] * { touch-action: pan-y; }"}</style>

      <div
        ref={host}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={(e) => end(e, false)}
        onPointerCancel={(e) => end(e, true)}
        style={{ position: "relative", flex: "1 1 auto", minHeight: 0, overflow: "hidden", touchAction: "pan-y" }}
      >
        {width > 0 &&
          labels.map((l, i) => {
            const left = cardLeft(i, index, drag, width);
            // Off screen by more than a card: nothing to draw.
            if (left > width + w || left < -2 * w) return null;
            return (
              <div
                key={l.id}
                role="tabpanel"
                data-qf-card=""
                aria-hidden={i !== index}
                style={{
                  position: "absolute",
                  top: 8,
                  bottom: 8,
                  left: 0,
                  width: w,
                  transform: `translateX(${left}px)`,
                  transition: dragging ? "none" : SLIDE,
                  border: "1px solid var(--qf-border)",
                  borderRadius: 10,
                  background: "var(--qf-surface)",
                  overflowY: "auto",
                  overflowX: "hidden",
                  // A neighbour is there to be seen, not touched mid-swipe.
                  pointerEvents: i === index ? "auto" : "none",
                }}
              >
                {isMounted(i, index) ? render(l.id) : null}
              </div>
            );
          })}
      </div>
    </div>
  );
}

/**
 * A view's two-letter chip: the first two letters of a single word, the
 * initials of two (`Fin St` -> `Fi`, `Bar Intel` -> `BI`). Fin St reads as its
 * first word on purpose: "FS" says nothing the tab says. 13F Funds reads `13F`.
 */
export function shortLabel(label: string): string {
  // Two letters of "13F Funds" are "1F", which says nothing.
  if (label === "13F Funds") return "13F";
  // Its initials would be "AR"; "AI" is what the tab is.
  if (label === "AI Review") return "AI";
  const words = label.trim().split(/\s+/);
  if (words.length > 1 && label !== "Fin St") return (words[0]![0]! + words[1]![0]!).toUpperCase();
  const w = words[0] ?? "";
  return w.slice(0, 1).toUpperCase() + w.slice(1, 2).toLowerCase();
}
